import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Lock, Mail, ArrowLeft } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function AdminLogin() {
  const { t } = useI18n();
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const r = await login(email, password);
    setLoading(false);
    if (r.ok) {
      toast.success("Welcome back");
      navigate("/admin");
    } else {
      toast.error(r.error || "Login failed");
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-900 flex items-center justify-center p-4 font-sans">
      <div className="w-full max-w-md">
        <Link to="/" className="tech-label inline-flex items-center gap-2 mb-6 hover:text-brand-red" data-testid="admin-back-home">
          <ArrowLeft size={14} /> Back to site
        </Link>
        <div className="bg-white border border-zinc-300 p-8 hard-shadow">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-12 w-12 bg-zinc-900 border-2 border-brand-red flex items-center justify-center text-white font-display font-black">
              GBS
            </div>
            <div>
              <div className="tech-label !text-[10px]">CMS · ADMIN</div>
              <h1 className="font-display font-black text-2xl tracking-tight">{t("admin.login")}</h1>
            </div>
          </div>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="admin-login-form">
            <div>
              <label className="tech-label mb-2 block">{t("admin.email")}</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <Input
                  data-testid="admin-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-9 h-11"
                  placeholder="admin@gbscollision.com"
                />
              </div>
            </div>
            <div>
              <label className="tech-label mb-2 block">{t("admin.password")}</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <Input
                  data-testid="admin-password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-9 h-11"
                  placeholder="••••••••"
                />
              </div>
            </div>
            <Button
              data-testid="admin-login-submit"
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-brand-red hover:bg-brand-redHover text-white font-bold tracking-wide rounded-none"
            >
              {loading ? "..." : t("admin.signIn")}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
